import { useState, useEffect, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Camera,
  CheckCircle2,
  AlertCircle,
  Scan,
  ShieldCheck,
  ArrowRight,
  RefreshCw,
  X,
  Smartphone,
} from 'lucide-react';
import { useAuth } from '../lib/AuthContext';
import { toast } from 'sonner';
import { exchangeVerificationToken } from '../lib/api/verification';
import { Html5Qrcode } from 'html5-qrcode';

interface ScanVerifyProps {
  onNavigate?: (page: string) => void;
}

type ScanState = 'idle' | 'scanning' | 'processing' | 'success' | 'error';

export function ScanVerify({ onNavigate }: ScanVerifyProps) {
  const { user, profile, refreshProfile } = useAuth();
  const [scanState, setScanState] = useState<ScanState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [scannedToken, setScannedToken] = useState<string | null>(null);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const [isScanning, setIsScanning] = useState(false);

  // Cleanup scanner on unmount
  useEffect(() => {
    return () => {
      if (scannerRef.current && isScanning) {
        scannerRef.current
          .stop()
          .catch((err) => console.error('Error stopping scanner:', err));
      }
    };
  }, [isScanning]);

  // Start camera scanning
  const startScanning = async () => {
    try {
      setCameraError(null);
      setError(null);
      setScanState('scanning');

      // Initialize scanner
      const scanner = new Html5Qrcode('qr-reader');
      scannerRef.current = scanner;

      // Start scanning
      await scanner.start(
        { facingMode: 'environment' }, // Use back camera on mobile
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
        },
        // Success callback
        async (decodedText) => {
          console.log('✅ QR Code scanned:', decodedText);
          setScannedToken(decodedText);
          setScanState('processing');

          // Stop scanning
          await scanner.stop();
          setIsScanning(false);

          // Process the token
          await processToken(decodedText);
        },
        // Error callback (ignore - happens frequently during scanning)
        () => {
          // Silent - this fires constantly while scanning
        }
      );

      setIsScanning(true);
      toast.success('Camera activated - position QR code in frame');
    } catch (err: any) {
      console.error('Camera error:', err);
      setCameraError(
        err.message || 'Unable to access camera. Please check permissions.'
      );
      setScanState('error');
      setIsScanning(false);

      // User-friendly error messages
      if (err.name === 'NotAllowedError') {
        setCameraError('Camera access denied. Please allow camera permissions and try again.');
      } else if (err.name === 'NotFoundError') {
        setCameraError('No camera found on this device.');
      } else if (err.name === 'NotReadableError') {
        setCameraError('Camera is in use by another application.');
      }
    }
  };

  // Stop camera scanning
  const stopScanning = async () => {
    if (scannerRef.current && isScanning) {
      try {
        await scannerRef.current.stop();
        setIsScanning(false);
        setScanState('idle');
        toast.info('Camera stopped');
      } catch (err) {
        console.error('Error stopping scanner:', err);
      }
    }
  };

  // Process scanned token
  const processToken = async (token: string) => {
    if (!user) {
      setError('Please sign in to verify your account');
      setScanState('error');
      return;
    }

    try {
      setScanState('processing');
      setError(null);

      // Call backend to exchange token
      const result = await exchangeVerificationToken(token, user.id);

      if (result.success) {
        console.log('✅ Verification successful:', result);
        
        // Refresh profile to get updated verification status
        await refreshProfile();
        
        setScanState('success');
        toast.success('Account verified successfully!');
      } else {
        setError(result.message || 'Verification failed');
        setScanState('error');
        toast.error('Verification failed');
      }
    } catch (err: any) {
      console.error('Verification error:', err);
      
      // Handle specific errors
      if (err.message?.includes('expired')) {
        setError('This verification code has expired. Please request a new one from your clinic.');
      } else if (err.message?.includes('used')) {
        setError('This verification code has already been used.');
      } else if (err.message?.includes('Invalid')) {
        setError('Invalid verification code. Please check with your clinic.');
      } else {
        setError(err.message || 'Failed to verify account. Please try again.');
      }
      
      setScanState('error');
      toast.error('Verification failed');
    }
  };

  // Manual token entry
  const handleManualEntry = () => {
    const token = prompt('Enter verification code:');
    if (token) {
      processToken(token.trim());
    }
  };

  // Reset to scan again
  const resetScan = () => {
    setScanState('idle');
    setError(null);
    setScannedToken(null);
    setCameraError(null);
  };

  // Render success state
  if (scanState === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8 gradient-mesh">
        <div className="w-full max-w-2xl">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <Card className="p-10 sm:p-12 text-center glass-card">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                className="w-24 h-24 rounded-full gradient-green flex items-center justify-center mx-auto mb-6 shadow-xl"
              >
                <CheckCircle2 className="w-12 h-12 text-white" />
              </motion.div>

              <h2 className="text-3xl mb-4">Account Verified!</h2>

              <div className="mb-8">
                <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary/10 border border-primary/20 mb-4">
                  <ShieldCheck className="w-5 h-5 text-primary" />
                  <span className="text-primary">Verified Patient</span>
                </div>
                <p className="text-lg text-muted-foreground">
                  Your account has been successfully verified by your clinic.
                  You can now access all platform features.
                </p>
              </div>

              <div className="space-y-3">
                <Button
                  className="w-full gradient-green hover:opacity-90 transition-opacity h-12"
                  onClick={() => onNavigate && onNavigate('discover')}
                >
                  Start Discovering Products
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => onNavigate && onNavigate('account')}
                >
                  View Account Settings
                </Button>
              </div>
            </Card>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-20 px-4 sm:px-6 lg:px-8 gradient-mesh">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8"
        >
          <h1 className="text-4xl mb-3">Scan to Verify</h1>
          <p className="text-xl text-muted-foreground">
            Scan the QR code provided by your clinic to verify your account
          </p>
        </motion.div>

        {/* Error Alert */}
        <AnimatePresence>
          {(error || cameraError) && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="mb-6"
            >
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error || cameraError}</AlertDescription>
              </Alert>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Main Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.6 }}
        >
          <Card className="p-8 glass-card">
            {/* Idle State - Start Scanning */}
            {scanState === 'idle' && (
              <div className="text-center">
                <div className="w-48 h-48 mx-auto mb-6 rounded-2xl bg-muted/30 flex items-center justify-center border-2 border-dashed border-border">
                  <Camera className="w-24 h-24 text-muted-foreground" />
                </div>

                <h2 className="text-2xl mb-4">Ready to Scan</h2>
                <p className="text-muted-foreground mb-8">
                  Position the QR code from your clinic within the camera frame.
                  Make sure you have good lighting and the code is clearly visible.
                </p>

                <div className="space-y-3">
                  <Button
                    onClick={startScanning}
                    className="w-full gradient-green hover:opacity-90 transition-opacity h-12"
                    size="lg"
                  >
                    <Camera className="w-5 h-5 mr-2" />
                    Start Camera
                  </Button>
                  <Button
                    onClick={handleManualEntry}
                    variant="outline"
                    className="w-full"
                  >
                    Enter Code Manually
                  </Button>
                </div>

                {/* Instructions */}
                <div className="mt-8 pt-6 border-t border-border">
                  <h3 className="text-sm font-medium mb-3">How it works:</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm text-muted-foreground">
                    <div className="flex flex-col items-center gap-2">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <Smartphone className="w-5 h-5 text-primary" />
                      </div>
                      <p>1. Visit your clinic</p>
                    </div>
                    <div className="flex flex-col items-center gap-2">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <Scan className="w-5 h-5 text-primary" />
                      </div>
                      <p>2. Scan QR code</p>
                    </div>
                    <div className="flex flex-col items-center gap-2">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <ShieldCheck className="w-5 h-5 text-primary" />
                      </div>
                      <p>3. Get verified</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Scanning State */}
            {scanState === 'scanning' && (
              <div className="text-center">
                <div className="relative mb-6">
                  {/* QR Scanner Container */}
                  <div
                    id="qr-reader"
                    className="mx-auto rounded-2xl overflow-hidden"
                    style={{ width: '100%', maxWidth: '500px' }}
                  />
                  
                  {/* Scanning Indicator */}
                  <div className="absolute top-4 left-1/2 -translate-x-1/2 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/90 text-white backdrop-blur-sm">
                    <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
                    <span className="text-sm font-medium">Scanning...</span>
                  </div>
                </div>

                <h2 className="text-2xl mb-3">Position QR Code</h2>
                <p className="text-muted-foreground mb-6">
                  Center the QR code within the camera frame
                </p>

                <div className="space-y-3">
                  <Button
                    onClick={stopScanning}
                    variant="outline"
                    className="w-full"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Stop Camera
                  </Button>
                </div>

                <Alert className="mt-6">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    <strong>Tip:</strong> Hold your device steady and ensure good lighting for best results.
                  </AlertDescription>
                </Alert>
              </div>
            )}

            {/* Processing State */}
            {scanState === 'processing' && (
              <div className="text-center py-12">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                  className="w-16 h-16 mx-auto mb-6"
                >
                  <RefreshCw className="w-16 h-16 text-primary" />
                </motion.div>
                <h2 className="text-2xl mb-3">Verifying...</h2>
                <p className="text-muted-foreground">
                  Processing your verification code with clinic
                </p>
              </div>
            )}

            {/* Error State */}
            {scanState === 'error' && (
              <div className="text-center">
                <div className="w-24 h-24 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-6">
                  <AlertCircle className="w-12 h-12 text-destructive" />
                </div>

                <h2 className="text-2xl mb-3">Verification Failed</h2>
                <p className="text-muted-foreground mb-8">
                  {error || cameraError || 'Unable to verify your account. Please try again.'}
                </p>

                <div className="space-y-3">
                  <Button
                    onClick={resetScan}
                    className="w-full gradient-green hover:opacity-90 transition-opacity"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Try Again
                  </Button>
                  <Button
                    onClick={handleManualEntry}
                    variant="outline"
                    className="w-full"
                  >
                    Enter Code Manually
                  </Button>
                  <Button
                    onClick={() => onNavigate && onNavigate('home')}
                    variant="ghost"
                    className="w-full"
                  >
                    Return Home
                  </Button>
                </div>
              </div>
            )}
          </Card>
        </motion.div>

        {/* Info Footer */}
        {scanState === 'idle' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="mt-6 text-center"
          >
            <Alert>
              <ShieldCheck className="h-4 w-4" />
              <AlertDescription>
                <strong>Need help?</strong> Contact your clinic if you don't have a verification code.
                Verification is required to submit reviews and access certain features.
              </AlertDescription>
            </Alert>
          </motion.div>
        )}
      </div>
    </div>
  );
}